import { Link } from 'react-router-dom';
import { Briefcase, CheckSquare, Mail, Ticket, UserPlus } from 'lucide-react';
import type { CrmTimelineEvent, CrmTimelineKind } from '../../types';
import { relativeTime } from '../../lib/crmInbox';
import EmptyState from '../EmptyState';

// One history, drawn from records that already exist. The icon says where a
// row came from, so a glance separates "they emailed" from "we logged a call"
// from "a ticket was opened" without reading a word.
const KIND_ICON: Record<CrmTimelineKind, React.ReactNode> = {
  message: <Mail className="w-3.5 h-3.5" />,
  activity: <CheckSquare className="w-3.5 h-3.5" />,
  ticket: <Ticket className="w-3.5 h-3.5" />,
  deal: <Briefcase className="w-3.5 h-3.5" />,
  contact: <UserPlus className="w-3.5 h-3.5" />,
};

const KIND_COLOR: Record<CrmTimelineKind, string> = {
  message: 'var(--scpa-primary)',
  activity: 'var(--scpa-status-ok)',
  ticket: 'var(--scpa-accent)',
  deal: 'var(--scpa-status-warning)',
  contact: 'var(--scpa-text-muted)',
};

const KIND_LABEL: Record<CrmTimelineKind, string> = {
  message: 'Email',
  activity: 'Activity',
  ticket: 'Ticket',
  deal: 'Opportunity',
  contact: 'Contact',
};

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const day = new Date(d); day.setHours(0, 0, 0, 0);
  const diff = Math.round((today.getTime() - day.getTime()) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return d.toLocaleDateString(undefined, { weekday: 'long' });
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
}

interface Props {
  events: CrmTimelineEvent[];
  /** Hide the type filter when the caller already scopes it. */
  filterable?: boolean;
  active?: CrmTimelineKind | 'all';
  onFilter?: (kind: CrmTimelineKind | 'all') => void;
  empty?: React.ReactNode;
}

export default function Timeline({ events, filterable, active = 'all', onFilter, empty }: Props) {
  const shown = active === 'all' ? events : events.filter(e => e.kind === active);
  const kinds = [...new Set(events.map(e => e.kind))];

  return (
    <div className="space-y-3">
      {filterable && kinds.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {(['all', ...kinds] as const).map(k => (
            <button
              key={k}
              type="button"
              onClick={() => onFilter?.(k as CrmTimelineKind | 'all')}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                active === k ? 'bg-primary text-white border-primary' : 'bg-surface text-muted border-line hover:text-fg'
              }`}
            >
              {k === 'all' ? 'Everything' : KIND_LABEL[k as CrmTimelineKind]}
            </button>
          ))}
        </div>
      )}

      {shown.length === 0 ? (
        <EmptyState>{empty ?? 'Nothing has happened here yet.'}</EmptyState>
      ) : (
        <ol className="relative">
          {shown.map((e, i) => {
            const newDay = i === 0 || dayLabel(e.at) !== dayLabel(shown[i - 1].at);
            const body = (
              <>
                <span
                  className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ color: KIND_COLOR[e.kind], backgroundColor: `color-mix(in srgb, ${KIND_COLOR[e.kind]} 14%, transparent)` }}
                  aria-hidden
                >
                  {KIND_ICON[e.kind]}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-fg">{e.title}</span>
                  {e.detail && <span className="block text-xs text-muted mt-0.5 line-clamp-2 whitespace-pre-wrap">{e.detail}</span>}
                  <span className="block text-[11px] text-muted mt-0.5">
                    {new Date(e.at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                    {e.actor ? ` · ${e.actor.name}` : ''}
                    {` · ${relativeTime(e.at)}`}
                  </span>
                </span>
              </>
            );
            return (
              <li key={e.id}>
                {newDay && (
                  <p className="text-[11px] font-bold uppercase tracking-wide text-muted pt-3 pb-1.5">{dayLabel(e.at)}</p>
                )}
                {e.link ? (
                  <Link to={e.link} className="flex items-start gap-2.5 py-1.5 px-2 -mx-2 rounded-md hover:bg-row-odd transition-colors">
                    {body}
                  </Link>
                ) : (
                  <div className="flex items-start gap-2.5 py-1.5 px-2 -mx-2">{body}</div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
